"use client";

import { CalendarIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import * as React from "react";
import type { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface DateRangePickerProps {
  onUpdate: (values: DateRange | undefined) => void;
  initialDateFrom?: string;
  initialDateTo?: string;
  align?: "start" | "center" | "end";
  value?: DateRange;
  showTime?: boolean;
}

function setTimeOnDate(date: Date, hours: number, minutes: number): Date {
  const copy = new Date(date);
  copy.setHours(hours, minutes, 0, 0);
  return copy;
}

export function DateRangePicker({
  onUpdate,
  initialDateFrom,
  initialDateTo,
  align = "center",
  value,
  showTime = true,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(
    value || {
      from: initialDateFrom ? new Date(initialDateFrom) : undefined,
      to: initialDateTo ? new Date(initialDateTo) : undefined,
    },
  );

  const fromId = React.useId();
  const toId = React.useId();
  const isMobile = useIsMobile();

  React.useEffect(() => {
    setDate(value);
  }, [value]);

  const handleSelect = (newDate: DateRange | undefined) => {
    if (newDate?.from) {
      const from = new Date(newDate.from);
      if (!from.getHours() && !from.getMinutes()) {
        from.setHours(0, 0, 0, 0);
      }
      if (newDate.to) {
        const to = new Date(newDate.to);
        if (to.getHours() === 0 && to.getMinutes() === 0) {
          to.setHours(23, 59, 59, 999);
        }
        setDate({ from, to });
        onUpdate({ from, to });
      } else {
        setDate({ from });
        onUpdate({ from });
      }
    } else {
      setDate(newDate);
      onUpdate(newDate);
    }
  };

  const handleTimeChange = (which: "from" | "to", value: string) => {
    if (!date?.from) return;
    const [hours, minutes] = value.split(":").map(Number);
    if (which === "from") {
      const newFrom = setTimeOnDate(date.from, hours, minutes);
      const newTo = date.to ? new Date(date.to) : newFrom;
      const next = { from: newFrom, to: newTo };
      setDate(next);
      onUpdate(next);
    } else if (date.to) {
      const newTo = setTimeOnDate(date.to, hours, minutes);
      const next = { from: new Date(date.from), to: newTo };
      setDate(next);
      onUpdate(next);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDate(undefined);
    onUpdate(undefined);
  };

  const formatDisplay = (d: Date) =>
    showTime ? format(d, "LLL dd, y HH:mm") : format(d, "LLL dd, y");

  const formatDisplayMobile = (d: Date) =>
    showTime ? format(d, "MMM d HH:mm") : format(d, "MMM d");

  return (
    <div className="grid gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal min-w-0",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate flex-1 min-w-0 text-left">
              {date?.from ? (
                date.to ? (
                  <>
                    {isMobile
                      ? `${formatDisplayMobile(date.from)} - ${formatDisplayMobile(date.to)}`
                      : `${formatDisplay(date.from)} - ${formatDisplay(date.to)}`}
                  </>
                ) : (
                  isMobile
                    ? formatDisplayMobile(date.from)
                    : formatDisplay(date.from)
                )
              ) : (
                <span>Pick a date</span>
              )}
            </span>
            {date?.from && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-2 rounded-sm opacity-70 hover:opacity-100 hover:bg-accent p-0.5 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("w-auto p-0", isMobile && "max-w-[calc(100vw-2rem)]")}
          align={align}
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={isMobile ? 1 : 2}
          />
          {showTime && date?.from && (
            <div className="flex flex-col sm:flex-row gap-4 p-4 border-t">
              {date.from && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={fromId}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Start
                  </label>
                  <input
                    id={fromId}
                    type="time"
                    value={format(date.from, "HH:mm")}
                    onChange={(e) => handleTimeChange("from", e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  />
                </div>
              )}
              {date.to && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor={toId}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    End
                  </label>
                  <input
                    id={toId}
                    type="time"
                    value={format(date.to, "HH:mm")}
                    onChange={(e) => handleTimeChange("to", e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  />
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
